import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { canonicalCoachOnboardingReasonIsSafe } from "./canonical-coach-onboarding.js";

const CoachLifecycleIdSchema = Type.String({ minLength: 1, maxLength: 512 });
const CoachLifecycleDateTimeSchema = Type.String({ format: "date-time" });
const CoachLifecycleSha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const CoachLifecycleIdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 96,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]*$",
});

export const CanonicalCoachLifecycleActionSchema = Type.Union([
  Type.Literal("DISABLE"),
  Type.Literal("REENABLE"),
]);
export type CanonicalCoachLifecycleAction = Static<typeof CanonicalCoachLifecycleActionSchema>;

export const CanonicalCoachLifecycleAccountStatusSchema = Type.Union([
  Type.Literal("NONE"),
  Type.Literal("PENDING"),
  Type.Literal("ACTIVE"),
  Type.Literal("DISABLED"),
  Type.Literal("MIGRATION_REVIEW"),
  Type.Literal("CONFLICT"),
]);
export type CanonicalCoachLifecycleAccountStatus = Static<
  typeof CanonicalCoachLifecycleAccountStatusSchema
>;

export const CanonicalCoachLifecycleAccountNextStepSchema = Type.Union([
  Type.Literal("ACTIVE_ACCOUNT_RETAINED"),
  Type.Literal("ACTIVE_ACCOUNT_CONTINUES"),
  Type.Literal("INVITATION_REQUIRED"),
  Type.Literal("ACCOUNT_RECOVERY_REQUIRED"),
]);
export type CanonicalCoachLifecycleAccountNextStep = Static<
  typeof CanonicalCoachLifecycleAccountNextStepSchema
>;

export const CanonicalCoachLifecycleBlockerCodeSchema = Type.Union([
  Type.Literal("SELF_ACTION_FORBIDDEN"),
  Type.Literal("PROFILE_NOT_ENABLED"),
  Type.Literal("PROFILE_NOT_DISABLED"),
  Type.Literal("WORKFORCE_BINDING_CONFLICT"),
  Type.Literal("EMPLOYMENT_NOT_ACTIVE"),
  Type.Literal("EMPLOYMENT_NOT_ENDED"),
  Type.Literal("MIGRATION_CONFLICT_OPEN"),
  Type.Literal("ACCOUNT_BINDING_CONFLICT"),
  Type.Literal("ACTIVE_OR_ASSIGNED_SERVICE"),
  Type.Literal("COACH_SHIFT_ACTIVE"),
  Type.Literal("ATTENDANCE_ACTIVE"),
  Type.Literal("QUEUE_ACTIVE"),
  Type.Literal("PENDING_COACH_ITEM"),
]);
export type CanonicalCoachLifecycleBlockerCode = Static<
  typeof CanonicalCoachLifecycleBlockerCodeSchema
>;

export const CanonicalCoachLifecycleBlockerResolutionCodeSchema = Type.Union([
  Type.Literal("USE_ANOTHER_MANAGER"),
  Type.Literal("REFRESH_PERSONNEL_RECORD"),
  Type.Literal("FINISH_OR_REASSIGN_SERVICE"),
  Type.Literal("END_COACH_SHIFT"),
  Type.Literal("SIGN_OUT_ATTENDANCE"),
  Type.Literal("REMOVE_FROM_QUEUE"),
  Type.Literal("RESOLVE_COACH_ITEM"),
]);
export type CanonicalCoachLifecycleBlockerResolutionCode = Static<
  typeof CanonicalCoachLifecycleBlockerResolutionCodeSchema
>;

export const CanonicalCoachLifecycleBlockerSchema = Type.Object(
  {
    code: CanonicalCoachLifecycleBlockerCodeSchema,
    count: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);
export type CanonicalCoachLifecycleBlocker = Static<typeof CanonicalCoachLifecycleBlockerSchema>;

/**
 * Current-store preview assembled from the accepted workforce projection and
 * live Gateway responsibilities. The token binds every business fact that is
 * relevant to the confirmation; POST still rebuilds and compares the preview.
 */
export const CanonicalCoachLifecyclePreviewSchema = Type.Object(
  {
    action: CanonicalCoachLifecycleActionSchema,
    profileId: CoachLifecycleIdSchema,
    storeId: CoachLifecycleIdSchema,
    personId: CoachLifecycleIdSchema,
    employmentId: CoachLifecycleIdSchema,
    profileStatus: Type.Union([Type.Literal("ENABLED"), Type.Literal("DISABLED")]),
    profileVersion: Type.Integer({ minimum: 1 }),
    coachConfigurationVersion: Type.Integer({ minimum: 1 }),
    personAggregateVersion: Type.Integer({ minimum: 1 }),
    employmentStatus: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("ACTIVE"),
      Type.Literal("REVIEW_REQUIRED"),
      Type.Literal("ENDED"),
    ]),
    employmentVersion: Type.Integer({ minimum: 1 }),
    projectionRevision: Type.Integer({ minimum: 1 }),
    projectionContentSha256: CoachLifecycleSha256Schema,
    accountStatus: CanonicalCoachLifecycleAccountStatusSchema,
    accountNextStep: CanonicalCoachLifecycleAccountNextStepSchema,
    activeSessionsToRevoke: Type.Integer({ minimum: 0 }),
    canApply: Type.Boolean(),
    historyWillBePreserved: Type.Literal(true),
    accountWillRemainUnchanged: Type.Literal(true),
    noAutomaticClockOut: Type.Literal(true),
    blockers: Type.Array(CanonicalCoachLifecycleBlockerSchema),
    previewToken: CoachLifecycleSha256Schema,
    previewedAt: CoachLifecycleDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CanonicalCoachLifecyclePreview = Static<typeof CanonicalCoachLifecyclePreviewSchema>;

export const ApplyCanonicalCoachLifecycleCommandSchema = Type.Object(
  {
    action: CanonicalCoachLifecycleActionSchema,
    profileId: CoachLifecycleIdSchema,
    expectedProfileVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    expectedCoachConfigurationVersion: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER - 1,
    }),
    expectedPersonAggregateVersion: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER - 1,
    }),
    expectedEmploymentId: CoachLifecycleIdSchema,
    expectedEmploymentVersion: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER - 1,
    }),
    expectedProjectionRevision: Type.Integer({ minimum: 1 }),
    previewToken: CoachLifecycleSha256Schema,
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: CoachLifecycleIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ApplyCanonicalCoachLifecycleCommand = Static<
  typeof ApplyCanonicalCoachLifecycleCommandSchema
>;

export const CanonicalCoachLifecycleResultSchema = Type.Object(
  {
    action: CanonicalCoachLifecycleActionSchema,
    profileId: CoachLifecycleIdSchema,
    personId: CoachLifecycleIdSchema,
    previousEmploymentId: CoachLifecycleIdSchema,
    employmentId: CoachLifecycleIdSchema,
    profileStatus: Type.Union([Type.Literal("ENABLED"), Type.Literal("DISABLED")]),
    profileVersion: Type.Integer({ minimum: 2 }),
    coachConfigurationVersion: Type.Integer({ minimum: 2 }),
    personAggregateVersion: Type.Integer({ minimum: 2 }),
    employmentStatus: Type.Union([Type.Literal("ACTIVE"), Type.Literal("ENDED")]),
    employmentVersion: Type.Integer({ minimum: 1 }),
    projectionRevision: Type.Integer({ minimum: 1 }),
    accountStatus: CanonicalCoachLifecycleAccountStatusSchema,
    accountNextStep: CanonicalCoachLifecycleAccountNextStepSchema,
    revokedSessionCount: Type.Integer({ minimum: 0 }),
    historyPreserved: Type.Literal(true),
    accountChanged: Type.Literal(false),
    automaticClockOutPerformed: Type.Literal(false),
    idempotencyKey: CoachLifecycleIdempotencyKeySchema,
    completedAt: CoachLifecycleDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CanonicalCoachLifecycleResult = Static<typeof CanonicalCoachLifecycleResultSchema>;

export const CANONICAL_COACH_LIFECYCLE_IDEMPOTENCY_PREFIX = "coach-lifecycle:";

export function canonicalCoachLifecycleIdempotencyKey(rootKey: string): string {
  return `${CANONICAL_COACH_LIFECYCLE_IDEMPOTENCY_PREFIX}${rootKey}`;
}

export function isApplyCanonicalCoachLifecycleCommand(
  value: unknown,
): value is ApplyCanonicalCoachLifecycleCommand {
  return (
    Value.Check(ApplyCanonicalCoachLifecycleCommandSchema, value) &&
    value.profileId === value.profileId.trim() &&
    value.expectedEmploymentId === value.expectedEmploymentId.trim() &&
    value.reason === value.reason.trim() &&
    canonicalCoachOnboardingReasonIsSafe(value.reason)
  );
}

export function isCanonicalCoachLifecyclePreview(
  value: unknown,
): value is CanonicalCoachLifecyclePreview {
  return (
    Value.Check(CanonicalCoachLifecyclePreviewSchema, value) &&
    value.canApply === (value.blockers.length === 0) &&
    value.accountNextStep ===
      canonicalCoachLifecycleAccountNextStep(value.action, value.accountStatus)
  );
}

export function canonicalCoachLifecycleBlockerResolution(
  code: CanonicalCoachLifecycleBlockerCode,
): CanonicalCoachLifecycleBlockerResolutionCode {
  switch (code) {
    case "SELF_ACTION_FORBIDDEN":
      return "USE_ANOTHER_MANAGER";
    case "PROFILE_NOT_ENABLED":
    case "PROFILE_NOT_DISABLED":
    case "WORKFORCE_BINDING_CONFLICT":
    case "EMPLOYMENT_NOT_ACTIVE":
    case "EMPLOYMENT_NOT_ENDED":
    case "MIGRATION_CONFLICT_OPEN":
    case "ACCOUNT_BINDING_CONFLICT":
      return "REFRESH_PERSONNEL_RECORD";
    case "ACTIVE_OR_ASSIGNED_SERVICE":
      return "FINISH_OR_REASSIGN_SERVICE";
    case "COACH_SHIFT_ACTIVE":
      return "END_COACH_SHIFT";
    case "ATTENDANCE_ACTIVE":
      return "SIGN_OUT_ATTENDANCE";
    case "QUEUE_ACTIVE":
      return "REMOVE_FROM_QUEUE";
    case "PENDING_COACH_ITEM":
      return "RESOLVE_COACH_ITEM";
  }
}

export function isCanonicalCoachLifecycleResult(
  value: unknown,
): value is CanonicalCoachLifecycleResult {
  if (!Value.Check(CanonicalCoachLifecycleResultSchema, value)) return false;
  if (
    value.accountNextStep !==
      canonicalCoachLifecycleAccountNextStep(value.action, value.accountStatus) ||
    value.employmentVersion !== value.profileVersion
  ) {
    return false;
  }
  if (value.action === "DISABLE") {
    return (
      value.profileStatus === "DISABLED" &&
      value.employmentStatus === "ENDED" &&
      value.employmentId === value.previousEmploymentId
    );
  }
  return (
    value.profileStatus === "ENABLED" &&
    value.employmentStatus === "ACTIVE" &&
    value.employmentId !== value.previousEmploymentId &&
    value.revokedSessionCount === 0
  );
}

export function canonicalCoachLifecycleAccountNextStep(
  action: CanonicalCoachLifecycleAction,
  accountStatus: CanonicalCoachLifecycleAccountStatus,
): CanonicalCoachLifecycleAccountNextStep {
  if (accountStatus === "ACTIVE") {
    return action === "DISABLE" ? "ACTIVE_ACCOUNT_RETAINED" : "ACTIVE_ACCOUNT_CONTINUES";
  }
  return accountStatus === "NONE" ? "INVITATION_REQUIRED" : "ACCOUNT_RECOVERY_REQUIRED";
}

export function canonicalCoachLifecycleResultMatchesCommand(
  result: CanonicalCoachLifecycleResult,
  command: ApplyCanonicalCoachLifecycleCommand,
): boolean {
  return (
    result.action === command.action &&
    result.profileId === command.profileId &&
    result.previousEmploymentId === command.expectedEmploymentId &&
    result.profileVersion === command.expectedProfileVersion + 1 &&
    result.coachConfigurationVersion === command.expectedCoachConfigurationVersion + 1 &&
    result.personAggregateVersion === command.expectedPersonAggregateVersion + 1 &&
    result.projectionRevision > command.expectedProjectionRevision &&
    result.idempotencyKey === command.idempotencyKey &&
    result.historyPreserved &&
    !result.accountChanged &&
    !result.automaticClockOutPerformed
  );
}
